import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../../hooks/useAuth';
import { getAuthErrorMessage, createValidationError } from '../../utils/authErrors';

export const SignUpForm = ({ onToggleMode, returnUrl }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const { signUp, signInWithGoogle } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Client-side validation with user-friendly messages
    if (!displayName.trim()) {
      const validationError = createValidationError('invalid-display-name');
      setError(getAuthErrorMessage(validationError));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      const validationError = createValidationError('passwords-dont-match');
      setError(getAuthErrorMessage(validationError));
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      const validationError = createValidationError('password-too-short');
      setError(getAuthErrorMessage(validationError));
      setLoading(false);
      return;
    }

    try {
      await signUp(email, password, displayName);
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');

    try {
      await signInWithGoogle();
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Box w="full" p={{ base: 4, md: 8 }} borderWidth={1} borderRadius="lg" shadow="lg">
      <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" textAlign="center" mb={{ base: 6, md: 8 }}>
        Join Fighter Bet Plus
      </Text>

      {error && (
        <Alert status="error" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      <VStack spacing={{ base: 4, md: 6 }}>
        {/* Google Sign-Up */}
        <Button
          leftIcon={<FcGoogle size="20" />}
          width="full"
          size="lg"
          height="56px"
          fontSize="md"
          fontWeight="semibold"
          bg="white"
          color="gray.700"
          border="2px solid"
          borderColor="gray.300"
          _hover={{
            bg: 'gray.50',
            borderColor: 'gray.400',
            transform: 'translateY(-2px)',
            boxShadow: 'xl',
          }}
          _active={{
            bg: 'gray.100',
            transform: 'translateY(0)',
          }}
          transition="all 0.2s"
          onClick={handleGoogleSignIn}
          isLoading={googleLoading}
          loadingText="Signing up with Google..."
        >
          Continue with Google
        </Button>

        {/* Or Separator */}
        <Text color="gray.400" fontSize="sm" fontWeight="medium" my={0}>
          or
        </Text>

        {/* Email Sign-Up Form */}
        <Box w="full">
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Display Name</FormLabel>
                <Input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Confirm Password</FormLabel>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                />
              </FormControl>

              <Button
                type="submit"
                colorScheme="red"
                width="full"
                size="lg"
                height="56px"
                fontSize="md"
                fontWeight="semibold"
                isLoading={loading}
                loadingText="Creating account..."
              >
                Sign Up with Email
              </Button>
            </VStack>
          </form>
        </Box>

        {/* Sign In Link */}
        <Text textAlign="center" fontSize="sm" color="gray.400">
          Already have an account?{' '}
          <Button variant="link" colorScheme="blue" onClick={onToggleMode} size="sm">
            Sign in
          </Button>
        </Text>
      </VStack>
    </Box>
  );
};
